import { FC, ReactNode } from "react";
import classNames from "classnames";

export type TabItem = { key: string; label: ReactNode; children?: ReactNode };

interface TabsProps {
  items: TabItem[];
  activeKey: string;
  onChange?: (key: string) => void;
  classNames?: { root?: string; header?: string; item?: string; active?: string; content?: string };
}

const Tabs: FC<TabsProps> = ({ items, activeKey, onChange, classNames: tabsClassNames }) => {
  const activeItem = items.find((item) => item.key === activeKey);

  return (
    <div className={classNames("flex w-full flex-col", tabsClassNames?.root)}>
      <div role="tablist" className={classNames("outline-border-subtle outline-b flex w-full", tabsClassNames?.header)}>
        {items.map((item) => {
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange?.(item.key)}
              className={classNames(
                "relative flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap p-3 text-sm font-medium transition-colors",
                tabsClassNames?.item,
                isActive ? classNames("text-font-active", tabsClassNames?.active) : "text-font-default hover:text-font-active",
              )}
            >
              {item.label}
              <span
                aria-hidden
                className={classNames(
                  "absolute inset-x-0 -bottom-px h-0.5 origin-center transition-transform duration-300 ease-out",
                  isActive ? "scale-x-100 bg-font-active" : "scale-x-0 bg-font-minimal",
                )}
              />
            </button>
          );
        })}
      </div>
      {activeItem?.children && (
        <div role="tabpanel" className={tabsClassNames?.content}>
          {activeItem.children}
        </div>
      )}
    </div>
  );
};

export default Tabs;
